import React from 'react';
import { message, Popconfirm, Radio, Spin, Tooltip, Upload } from 'antd';
import { DeleteOutlined, FileOutlined, InboxOutlined } from '@ant-design/icons';
import apiClient from '../../../utils/apiService';
import { formatDisplayDate } from '../../../utils/dateDisplay';
import RemarkInlineEditor from './RemarkInlineEditor';

const ATTACHMENT_TIME_FORMAT = 'MM/DD/YY HH:mm:ss';

const AnAttachmentsPanel = React.memo(function AnAttachmentsPanel({
  hblNo,
  anAttachments,
  anAttachmentsLoading,
  anAttachmentsError,
  selectedAnAttachment,
  setSelectedAnAttachment,
  anEmailType,
  setAnEmailType,
  getFileIcon,
  renderAttachmentPreview,
  editingRemarkId,
  editingRemarkType,
  setEditingRemarkId,
  setEditingRemarkType,
  savingRemark,
  handleUpdateRemark,
  handleDeleteAttachment,
}) {
  const [anUploading, setAnUploading] = React.useState(false);
  const [files, setFiles] = React.useState(anAttachments);
  const [filesLoading, setFilesLoading] = React.useState(anAttachmentsLoading);
  const [filesError, setFilesError] = React.useState(anAttachmentsError);
  const [messageApi, contextHolder] = message.useMessage();

  React.useEffect(() => {
    setFiles(anAttachments);
    setFilesLoading(anAttachmentsLoading);
    setFilesError(anAttachmentsError);
  }, [anAttachments, anAttachmentsLoading, anAttachmentsError]);

  const refreshFiles = React.useCallback(async () => {
    try {
      if (!hblNo) return;
      const res = await apiClient.get(`/shipping-order/an-attachments/${hblNo}?emailType=${anEmailType}`);
      const { data } = res?.data || {};
      const list = Array.isArray(data) ? data : [];
      setFiles(list);
    } catch (e) {
      console.error('refresh an files failed', e);
    }
  }, [hblNo, anEmailType]);

  const handleUpload = React.useCallback(async ({ file, onSuccess, onError }) => {
    try {
      setAnUploading(true);
      messageApi.success('Uploading AN/PU attachment, please wait...');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('hblNo', hblNo);
      formData.append('emailType', anEmailType);

      const response = await apiClient.post('/shipping-order/upload-an-attachment', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      messageApi.success(`${file.name} uploaded successfully`);
      onSuccess(response.data);
      await refreshFiles();
    } catch (error) {
      console.error('Upload error:', error);
      messageApi.error('Upload failed');
      onError(error);
    } finally {
      setAnUploading(false);
    }
  }, [hblNo, anEmailType, messageApi, refreshFiles]);

  const handleDelete = React.useCallback(async (item) => {
    await handleDeleteAttachment(item);
    await refreshFiles();
  }, [handleDeleteAttachment, refreshFiles]);

  const handleSaveRemark = React.useCallback(async (itemId, remark, type) => {
    const success = await handleUpdateRemark(itemId, remark, type);
    if (success !== false) {
      setFiles(prev => prev.map(item =>
        item.id === itemId ? { ...item, remark } : item
      ));
    }
  }, [handleUpdateRemark]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {contextHolder}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', gap: '16px', minHeight: 0 }}>
        <div style={{ width: '30%', minWidth: '300px', display: 'flex', flexDirection: 'column', gap: '16px', minHeight: 0 }}>
          <Upload.Dragger
            name="file"
            multiple={false}
            customRequest={handleUpload}
            showUploadList={false}
            disabled={anUploading}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Click or drag file to upload AN/PU attachment</p>
            <p className="ant-upload-hint" style={{ color: '#fe4d4e' }}>
              Attachments in [A/N P/U attachments] will be sent to clients.<br /> Do NOT upload MA/N files !!
            </p>
          </Upload.Dragger>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '14px', color: '#666', flexShrink: 0 }}>Mail Type:</span>
            <Radio.Group
              value={anEmailType}
              onChange={(e) => setAnEmailType(e.target.value)}
              size="small"
            >
              <Radio value={0}>AN Mail</Radio>
              <Radio value={1}>PU Mail</Radio>
            </Radio.Group>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid #f0f0f0', borderRadius: '4px', backgroundColor: '#fff' }}>
            {filesLoading ? (
              <div className="email-loading" style={{ padding: '20px' }}>
                <Spin tip="Loading attachments..." size="small" />
              </div>
            ) : filesError ? (
              <div className="email-empty">{filesError}</div>
            ) : !files || files.length === 0 ? (
              <div className="email-empty">No AN/PU attachments yet</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', padding: '8px' }}>
                {files.map((item, index) => {
                  const itemId = item.id || index;
                  const isSelected = selectedAnAttachment && selectedAnAttachment.id === itemId;
                  return (
                    <div
                      key={itemId}
                      onClick={() => setSelectedAnAttachment(item)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                        padding: '12px',
                        cursor: 'pointer',
                        border: isSelected ? '1px solid #91d5ff' : '1px solid #f0f0f0',
                        borderLeft: isSelected ? '3px solid #1890ff' : '3px solid transparent',
                        borderRadius: '8px',
                        backgroundColor: isSelected ? '#e6f7ff' : '#fff',
                        marginBottom: '8px',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', paddingTop: 2 }}>
                          {getFileIcon(item.fileName)}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Tooltip title={item.fileName}>
                            <div style={{ fontSize: '13px', fontWeight: 500, lineHeight: 1.5, wordBreak: 'break-word' }}>
                              {item.fileName}
                            </div>
                          </Tooltip>
                          <div style={{ marginTop: '6px', fontSize: '12px', color: '#999', display: 'flex', flexDirection: 'column', gap: 4 }}>
                            <span>{Math.round(item.fileSize / 1024 || 0)} KB</span>
                            <span>Uploaded by: {item.createBy || '--'}</span>
                            <span>{item.createTime ? formatDisplayDate(item.createTime, ATTACHMENT_TIME_FORMAT) : '--'}</span>
                          </div>
                        </div>
                      </div>
                      <div
                        style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 6, flexShrink: 0 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {!!item.id && (
                          <Popconfirm
                            title="Delete Attachment"
                            description={`Are you sure you want to delete "${item?.fileName || 'this file'}"?`}
                            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(item)}
                            placement="topRight"
                            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                          >
                            <Tooltip title="Delete attachment">
                              <div style={{
                                width: '22px', height: '22px', borderRadius: '50%',
                                backgroundColor: '#fff1f0', border: '1px solid #ffccc7',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
                              }}><DeleteOutlined style={{ fontSize: '11px', color: '#ff4d4f' }} /></div>
                            </Tooltip>
                          </Popconfirm>
                        )}
                      </div>
                      <RemarkInlineEditor
                        itemId={itemId}
                        type="an"
                        remark={item.remark}
                        editingRemarkId={editingRemarkId}
                        editingRemarkType={editingRemarkType}
                        setEditingRemarkId={setEditingRemarkId}
                        setEditingRemarkType={setEditingRemarkType}
                        savingRemark={savingRemark}
                        onSave={handleSaveRemark}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={{ width: '70%', minWidth: '400px', border: '1px solid #d9d9d9', borderRadius: '4px', backgroundColor: '#fafafa', minHeight: 0 }}>
          {selectedAnAttachment ? (
            <div className="attachment-preview-content" style={{ height: '100%', overflow: 'auto' }}>
              {renderAttachmentPreview()}
            </div>
          ) : (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '14px' }}>
              <div style={{ textAlign: 'center' }}>
                <FileOutlined style={{ fontSize: '48px', color: '#d9d9d9', marginBottom: '16px' }} />
                <p>Select a file to preview</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default AnAttachmentsPanel;
